import {
	ApiResult_List_CombinedOfferV7Response__,
	CancelablePromise,
	CancelRequestDto,
	InsuranceOfferRequest,
	InsuranceOfferResponse,
	InsuranceRequestDto,
	NjBookingClient,
	OfferRequest,
	PaymentRequestDto,
	PrebookRequestDto,
	TicketResponseDto
} from '../http/nj-booking';
import {serverAddress} from "../helpers/utils";
import {i18n} from "../utils/I18n";
import {ApiRequestOptions} from "../http/nj-booking/core/ApiRequestOptions";
import {ApiResult} from "../http/nj-booking/core/ApiResult";
import {
	catchErrorCodes,
	getFormData,
	getHeaders,
	getRequestBody,
	getResponseBody,
	getResponseHeader,
	sendRequest
} from "../http/nj-booking/core/request";
import {SessionService} from "./session.service";

export class RestService {

	private static _instance: RestService;

	static get instance(): RestService {
		if (!this._instance) {
			this._instance = new RestService(serverAddress());
		}
		return this._instance;
	}

	private readonly client: NjBookingClient;

	constructor(baseUrl: string) {
		this.client = new NjBookingClient({
			BASE: baseUrl,
			HEADERS: async () => ({
				'X-Token': SessionService.instance.getToken(),
			})
		});
	}

	private async requestBlob(options: ApiRequestOptions): Promise<Blob> {
		// The generated client does not support returning as Blob natively, so we hack the relevant parts
		// See https://github.com/ferdikoomen/openapi-typescript-codegen/issues/1739#issuecomment-1713927669
		return new CancelablePromise(async (resolve, reject, onCancel) => {
			try {
				const config = this.client.request.config;
				const url = config.BASE + options.url;
				const formData = getFormData(options);
				const body = getRequestBody(options);
				const headers = await getHeaders(config, options);

				if (!onCancel.isCancelled) {
					const response = await sendRequest(config, options, url, body, formData, headers, onCancel);
					const responseBody = response.status === 200
						? await response.blob()
						: await getResponseBody(response);
					const responseHeader = getResponseHeader(response, options.responseHeader);

					const result: ApiResult = {
						url,
						ok: response.ok,
						status: response.status,
						statusText: response.statusText,
						body: responseHeader ?? responseBody,
					};

					catchErrorCodes(options, result);

					resolve(result.body);
				}
			} catch (error) {
				reject(error);
			}
		});
	}

	start() {
		return this.client.default.start({lang: i18n.getLang()});
	}

	getStation(eva: string) {
		return this.client.default.getStation(i18n.getLang(), eva);
	}

	findConnections(evaFrom: string, evaTo: string, date: string, skip?: number) {
		return this.client.default.findConnections(evaFrom, evaTo, date, skip);
	}

	getOffers(request: OfferRequest | any): CancelablePromise<ApiResult_List_CombinedOfferV7Response__ | any> {
		// TODO Update existing type declarations to match the generated ones
		return this.client.default.getOffers(request);
	}

	getInsuranceOffer(request: InsuranceOfferRequest): Promise<InsuranceOfferResponse> {
		return this.client.default.getInsuranceOffer(request, i18n.getLang());
	}

	createInsuranceContract(request: InsuranceRequestDto) {
		return this.client.default.createInsuranceContract(request);
	}

	prebook(request: PrebookRequestDto) {
		return this.client.default.prebook(request);
	}

	paymentInitialization(request: PaymentRequestDto) {
		return this.client.default.paymentInitialization(request);
	}

	getBookingValidity(ticketId: string) {
		return this.client.default.getBookingValidity(ticketId);
	}

	finalizeBooking(token: string) {
		return this.client.default.finalize({token, lang: i18n.getLang()});
	}

	getTicketData(ticketId: string): Promise<TicketResponseDto> {
		return this.client.default.getTicketData(ticketId, i18n.getLang());
	}

	resendConfirmationEmail(ticketId: string) {
		return this.client.default.resendConfirmationEmail({ticketId, lang: i18n.getLang()});
	}

	getTicketLink(ticketId: string) {
		return this.client.default.getTicketLink(ticketId, i18n.getLang());
	}

	createPdfTicket(ticketId: string) {
		return this.requestBlob({
			method: 'GET',
			url: `/fulfillment/createPDF?ticketId=${ticketId}&language=${i18n.getLang()}`,
		})
	}

	getCancelCondition(ticketId: string) {
		return this.client.default.cancelCondition({ticketId});
	}

	cancelBooking(body: CancelRequestDto) {
		return this.client.default.cancel(body);
	}

	getTaxCertificate(ticketId: string) {
		return this.requestBlob({
			method: 'GET',
			url: '/booking/taxCertificate/' + ticketId,
		});
	}
}
